/**
 * AgentChatContext - Global state for agent floating bubble and chat panel
 *
 * Supports multiple agents (research, portfolio, etc.) with an explicit selector.
 * The activeAgent state determines which agent handles new chat sessions.
 *
 * IMPORTANT: This file must ONLY import from 'react' to avoid circular dependencies.
 * Components using this context should be descendants of AgentChatProvider.
 *
 * Page Context Types:
 * - 'landing': Home/Landing page
 * - 'data-explorer': Data Explorer with filters/results
 * - 'company-detail': Individual company view
 * - 'investor-detail': Individual investor view
 * - 'deal-detail': Individual deal view
 * - 'tools': Tools hub page
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Agent configuration — static registry of agents available on the platform.
 * To add a new agent, add an entry here. Backend already has the agent registered.
 */
// Per-agent SVG icons embedded as data URLs (no API dependency)
export const AGENT_ICONS = {
  research: `data:image/svg+xml;charset=utf-8,${encodeURIComponent('<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><circle cx="18" cy="17" r="9" fill="none" stroke="white" stroke-width="3" stroke-opacity="0.9"/><circle cx="18" cy="17" r="4" fill="white" fill-opacity="0.5"/><line x1="25" y1="24" x2="32" y2="31" stroke="white" stroke-width="3.5" stroke-linecap="round" stroke-opacity="0.85"/><circle cx="12" cy="11" r="1.5" fill="white" fill-opacity="0.9"/><circle cx="22" cy="12" r="1.5" fill="white" fill-opacity="0.9"/><circle cx="17" cy="22" r="1.5" fill="white" fill-opacity="0.9"/></svg>')}`,
  portfolio: `data:image/svg+xml;charset=utf-8,${encodeURIComponent('<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><rect x="7" y="22" width="7" height="12" rx="1.5" fill="white" fill-opacity="0.8"/><rect x="16.5" y="14" width="7" height="20" rx="1.5" fill="white" fill-opacity="0.9"/><rect x="26" y="8" width="7" height="26" rx="1.5" fill="white" fill-opacity="1"/><line x1="5" y1="35" x2="35" y2="35" stroke="white" stroke-width="2" stroke-opacity="0.5" stroke-linecap="round"/></svg>')}`,
  navi: `data:image/svg+xml;charset=utf-8,${encodeURIComponent('<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><polygon points="20,8 28,20 20,32 12,20" fill="white" fill-opacity="0.95"/><ellipse cx="10" cy="17" rx="6" ry="9" fill="white" fill-opacity="0.45" transform="rotate(-12 10 17)"/><ellipse cx="30" cy="17" rx="6" ry="9" fill="white" fill-opacity="0.45" transform="rotate(12 30 17)"/><circle cx="20" cy="20" r="3.5" fill="white" fill-opacity="0.7"/></svg>')}`,
  shared: `data:image/svg+xml;charset=utf-8,${encodeURIComponent('<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="5" fill="white" fill-opacity="0.95"/><circle cx="20" cy="20" r="13" fill="none" stroke="white" stroke-width="1.5" stroke-opacity="0.4"/><circle cx="20" cy="7" r="3" fill="white" fill-opacity="0.8"/><circle cx="33" cy="20" r="3" fill="white" fill-opacity="0.8"/><circle cx="20" cy="33" r="3" fill="white" fill-opacity="0.8"/><circle cx="7" cy="20" r="3" fill="white" fill-opacity="0.8"/><line x1="20" y1="10" x2="20" y2="15" stroke="white" stroke-width="1.5" stroke-opacity="0.5"/><line x1="30" y1="20" x2="25" y2="20" stroke="white" stroke-width="1.5" stroke-opacity="0.5"/><line x1="20" y1="30" x2="20" y2="25" stroke="white" stroke-width="1.5" stroke-opacity="0.5"/><line x1="10" y1="20" x2="15" y2="20" stroke="white" stroke-width="1.5" stroke-opacity="0.5"/></svg>')}`,
};

export const AGENT_CONFIG = {
  research: {
    name: 'Research Agent',
    description: 'Market research, PitchBook data, deal sourcing',
    icon: AGENT_ICONS.research,
    colors: {
      primary: '#2C3E50',
      secondary: '#34495e',
      bubble: '#1a2744',
      bubbleEnd: '#243656',
      shadow: 'rgba(44, 62, 80, 0.3)',
    },
  },
  portfolio: {
    name: 'Portfolio Agent',
    description: 'Our investments, stakeholders, CRM operations',
    icon: AGENT_ICONS.portfolio,
    colors: {
      primary: '#1B5E20',
      secondary: '#2E7D32',
      bubble: '#1a3a1a',
      bubbleEnd: '#234d23',
      shadow: 'rgba(27, 94, 32, 0.3)',
    },
  },
  navi: {
    name: 'Navi',
    description: 'Slack messaging, email, team communication',
    icon: AGENT_ICONS.navi,
    colors: {
      primary: '#4A148C',
      secondary: '#6A1B9A',
      bubble: '#2a1050',
      bubbleEnd: '#3a1a6a',
      shadow: 'rgba(74, 20, 140, 0.3)',
    },
  },
};

const AgentChatContext = createContext(null);

export function AgentChatProvider({ children }) {
  const [activeAgent, setActiveAgent] = useState('research');
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const [searchContext, setSearchContext] = useState(null);
  const [pageContext, setPageContext] = useState(null);

  // Open chat panel (optionally with search context and/or specific agent)
  const openChat = useCallback((context = null, agent = null) => {
    if (agent) setActiveAgent(agent);
    if (context) setSearchContext(context);
    setChatPanelOpen(true);
  }, []);

  // Close chat panel
  const closeChat = useCallback(() => {
    setChatPanelOpen(false);
  }, []);

  // Switch active agent (panel stays open, session resets in AgentChatPanel)
  const switchAgent = useCallback((agent) => {
    setActiveAgent(agent);
  }, []);

  // Update search context (e.g., when new search results come in)
  const updateSearchContext = useCallback((context) => {
    setSearchContext(context);
  }, []);

  // Clear search context
  const clearSearchContext = useCallback(() => {
    setSearchContext(null);
  }, []);

  /**
   * Update page context - called by pages when they mount or their data changes
   * @param {string} pageType - Type of page ('company-detail', 'investor-detail', etc.)
   * @param {object} entityData - Main entity data (company, investor, deal info)
   * @param {object} viewState - Current view state (filters, selected tabs, etc.)
   */
  const updatePageContext = useCallback((pageType, entityData = null, viewState = null) => {
    setPageContext({
      pageType,
      entityData,
      viewState,
      timestamp: Date.now()
    });
  }, []);

  // Clear page context (when navigating away)
  const clearPageContext = useCallback(() => {
    setPageContext(null);
  }, []);

  const value = {
    activeAgent,
    chatPanelOpen,
    searchContext,
    pageContext,
    openChat,
    closeChat,
    switchAgent,
    updateSearchContext,
    clearSearchContext,
    updatePageContext,
    clearPageContext
  };

  return (
    <AgentChatContext.Provider value={value}>
      {children}
    </AgentChatContext.Provider>
  );
}

export function useAgentChat() {
  const context = useContext(AgentChatContext);
  if (!context) {
    throw new Error('useAgentChat must be used within an AgentChatProvider');
  }
  return context;
}

// Backwards-compatible alias — existing page imports don't need to change yet
export const useResearchAgent = useAgentChat;
export const ResearchAgentProvider = AgentChatProvider;

export default AgentChatContext;
